import React, { useEffect, useState, useRef } from 'react';
import { SessionResult, DAILY_GOAL_MINUTES } from '../../types';
import { Button } from './index';
import { useSounds } from '../../hooks';

interface PracticeCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: SessionResult | null;
  onContinue?: () => void;
  onGoHome?: () => void;
}

const PracticeCompleteModal: React.FC<PracticeCompleteModalProps> = ({
  isOpen,
  onClose,
  result,
  onContinue,
  onGoHome,
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const { playSound } = useSounds();
  const hasPlayedSoundRef = useRef(false);

  useEffect(() => {
    if (isOpen && result) {
      // Play sound when modal opens
      if (!hasPlayedSoundRef.current) {
        hasPlayedSoundRef.current = true;
        if (result.goalReached) {
          // Delay goal reached fanfare to sync with confetti
          setTimeout(() => playSound('goalReached'), 500);
        } else {
          playSound('complete');
        }
      }

      // Animate progress from 0 to actual value
      const startProgress = Math.max(0, result.dailyProgress - (result.durationMinutes / DAILY_GOAL_MINUTES) * 100);
      setAnimatedProgress(startProgress);

      // Animate to final progress
      const timer = setTimeout(() => {
        setAnimatedProgress(result.dailyProgress);
      }, 100);

      // Show confetti if goal reached
      if (result.goalReached) {
        const confettiTimer = setTimeout(() => {
          setShowConfetti(true);
        }, 600);
        return () => {
          clearTimeout(timer);
          clearTimeout(confettiTimer);
        };
      }

      return () => clearTimeout(timer);
    } else {
      hasPlayedSoundRef.current = false;
    }
  }, [isOpen, result, playSound]);

  if (!isOpen || !result) return null;

  const circumference = 2 * Math.PI * 70;
  const strokeDashoffset = circumference - (animatedProgress / 100) * circumference;

  // Get motivational message based on progress
  const getMessage = () => {
    if (result.goalReached) {
      return {
        title: 'Amazing! Goal Reached!',
        subtitle: 'You completed your daily goal!',
        emoji: '\u{1F3C6}',
      };
    }
    if (result.dailyProgress >= 75) {
      return {
        title: 'Almost There!',
        subtitle: 'Just a little more to reach your goal!',
        emoji: '\u{1F4AA}',
      };
    }
    if (result.dailyProgress >= 50) {
      return {
        title: 'Great Progress!',
        subtitle: 'You\'re halfway to your daily goal!',
        emoji: '\u{1F31F}',
      };
    }
    if (result.dailyProgress >= 25) {
      return {
        title: 'Good Start!',
        subtitle: 'Keep going, you\'re doing great!',
        emoji: '\u{1F44D}',
      };
    }
    return {
      title: 'Great Job!',
      subtitle: 'Every minute counts!',
      emoji: '\u{2728}',
    };
  };

  const message = getMessage();

  // Get session type icon
  const getSessionIcon = () => {
    switch (result.sessionType) {
      case 'grammar':
        return '\u{1F4DD}';
      case 'reading':
        return '\u{1F4DA}';
      case 'listening':
        return '\u{1F3A7}';
      case 'speaking':
        return '\u{1F399}';
      default:
        return '\u{2B50}';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      {/* Confetti animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <span
                className="text-2xl"
                style={{
                  color: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA'][
                    Math.floor(Math.random() * 6)
                  ],
                }}
              >
                {['\u2B50', '\u{1F389}', '\u{1F31F}', '\u2728', '\u{1F3C6}', '\u{1F4AB}'][Math.floor(Math.random() * 6)]}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl dark:shadow-slate-900 max-w-sm w-full p-8 text-center animate-pop relative overflow-hidden">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="text-4xl">{'\u{1F433}'}</span>
          <span className="text-2xl font-bold text-kids-primary dark:text-indigo-400">Litrlly Kids</span>
        </div>

        {/* Emoji and Title */}
        <div className="text-6xl mb-4 animate-bounce-slow">{message.emoji}</div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{message.title}</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{message.subtitle}</p>

        {/* Circular Progress */}
        <div className="relative w-40 h-40 mx-auto mb-6">
          <svg className="w-full h-full transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="80"
              cy="80"
              r="70"
              className="stroke-gray-200 dark:stroke-slate-600"
              strokeWidth="10"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="80"
              cy="80"
              r="70"
              stroke={result.goalReached ? '#10B981' : '#6366F1'}
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold text-gray-800 dark:text-white">
              {Math.round(animatedProgress)}%
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {result.dailyMinutes}/{DAILY_GOAL_MINUTES} min
            </span>
          </div>
        </div>

        {/* Session Stats */}
        <div className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <span className="text-2xl">{getSessionIcon()}</span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 capitalize">{result.sessionType}</p>
            </div>
            <div className="h-8 w-px bg-gray-300 dark:bg-slate-600"></div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800 dark:text-white">+{result.durationMinutes}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">minutes</p>
            </div>
            {result.score !== undefined && (
              <>
                <div className="h-8 w-px bg-gray-300 dark:bg-slate-600"></div>
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800 dark:text-white">{Math.round(result.score)}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">score</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {onContinue && (
            <Button onClick={onContinue} variant="primary" size="large" fullWidth>
              Continue Practicing
            </Button>
          )}
          {onGoHome && (
            <Button onClick={onGoHome} variant="secondary" size="medium" fullWidth>
              Back to Home
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticeCompleteModal;
